'use client';

import { c as _c } from "react/compiler-runtime";
import type { ActionDispatch } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { TopNavV5MobileAction, TopNavV5MobileState } from '@src/components/TopNavV5/TopNavV5Mobile/useTopNavV5MobileReducer';
import { useTopNavV5MobileReducer } from '@src/components/TopNavV5/TopNavV5Mobile/useTopNavV5MobileReducer';
import { useSession } from '@src/hooks/useSession';
import { useUnlocalizedPathname } from '@src/hooks/useUnlocalizedPathname';
import { useBreakpoints } from '@src/utils/breakpoints.util';
import type { TopNavV5MenuItem, TopNavV5Props } from './TopNavV5Props';
const TOP_NAV_DESKTOP_HEIGHT = 48;
interface TopNavV5ContextType extends TopNavV5Props {
  expandedMenu: TopNavV5MenuItem | null;
  setExpandedMenu: (menu: TopNavV5MenuItem | null) => void;
  leftMenuIndex: number;
  setLeftMenuIndex: (index: number) => void;
  isExpanded: boolean;
  setExpanded: (isExpanded: boolean) => void;
  isAccountExpanded: boolean;
  setAccountExpanded: (isExpanded: boolean) => void;
  mobileState: TopNavV5MobileState;
  mobileDispatch: ActionDispatch<[action: TopNavV5MobileAction]>;
  topNavDesktopHeight: number;
  topNavDesktopTopOffset: number;
}
export const TopNavV5Context = createContext<TopNavV5ContextType>({
  expandedMenu: null,
  setExpandedMenu: () => {},
  leftMenuIndex: 0,
  setLeftMenuIndex: () => {},
  isExpanded: false,
  setExpanded: () => {},
  isAccountExpanded: false,
  setAccountExpanded: () => {},
  mobileState: {
    state: 'closed',
    previousState: null,
    menuItems: [],
    leftMenu: null,
    leftMenuIndex: null,
    subMenu: null,
    miniMenu: null
  },
  mobileDispatch: () => {},
  topNavDesktopHeight: TOP_NAV_DESKTOP_HEIGHT,
  topNavDesktopTopOffset: TOP_NAV_DESKTOP_HEIGHT
});
export const TopNavV5Provider = t0 => {
  const $ = _c(20);
  const {
    children,
    topNavProps
  } = t0;
  const {
    isComingFromLogin
  } = useSession();
  const pathname = useUnlocalizedPathname();
  const {
    isDesktop
  } = useBreakpoints();
  const [isExpanded, setExpanded] = useState(false);
  const [isAccountExpanded, setAccountExpanded] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [leftMenuIndex, setLeftMenuIndex] = useState(0);
  const [mobileState, mobileDispatch] = useTopNavV5MobileReducer(topNavProps.items);
  const topNavDesktopHeight = TOP_NAV_DESKTOP_HEIGHT;
  const [pencilBannerDesktopHeight, setPencilBannerDesktopHeight] = useState(0);
  let t1;
  let t2;
  if ($[0] !== isDesktop) {
    t1 = () => {
      if (!isDesktop) {
        return;
      }
      const visibleBanner = [...document.querySelectorAll("[data-builder-component=\"top-banner\"]")].find(_temp);
      if (visibleBanner) {
        setPencilBannerDesktopHeight(visibleBanner.clientHeight);
      }
    };
    t2 = [isDesktop];
    $[0] = isDesktop;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== isComingFromLogin || $[4] !== isDesktop || $[5] !== pathname) {
    t3 = () => {
      const shouldExpandAccount = isDesktop && isComingFromLogin && !pathname.startsWith("/account");
      setAccountExpanded(shouldExpandAccount);
    };
    t4 = [isComingFromLogin, pathname, isDesktop];
    $[3] = isComingFromLogin;
    $[4] = isDesktop;
    $[5] = pathname;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  const t5 = TOP_NAV_DESKTOP_HEIGHT + pencilBannerDesktopHeight;
  let t6;
  if ($[8] !== expandedMenu || $[9] !== isAccountExpanded || $[10] !== isExpanded || $[11] !== leftMenuIndex || $[12] !== mobileDispatch || $[13] !== mobileState || $[14] !== t5 || $[15] !== topNavProps) {
    t6 = {
      ...topNavProps,
      isExpanded,
      setExpanded,
      leftMenuIndex,
      setLeftMenuIndex,
      expandedMenu,
      setExpandedMenu,
      isAccountExpanded,
      setAccountExpanded,
      mobileState,
      mobileDispatch,
      topNavDesktopHeight: TOP_NAV_DESKTOP_HEIGHT,
      topNavDesktopTopOffset: t5
    };
    $[8] = expandedMenu;
    $[9] = isAccountExpanded;
    $[10] = isExpanded;
    $[11] = leftMenuIndex;
    $[12] = mobileDispatch;
    $[13] = mobileState;
    $[14] = t5;
    $[15] = topNavProps;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const value = t6;
  let t7;
  if ($[17] !== children || $[18] !== value) {
    t7 = <TopNavV5Context.Provider value={value}>{children}</TopNavV5Context.Provider>;
    $[17] = children;
    $[18] = value;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
};
export const useTopNavV5 = () => {
  const context = useContext(TopNavV5Context);
  if (!context) {
    throw new Error("useTopNavV5 must be used within a TopNavV5Provider");
  }
  return context;
};
function _temp(x) {
  const rect = x.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
}